import * as React from "react"
import { ActivityEventCard } from "./ActivityEventCard"
import type { BaseActivityEvent } from "./types"
import type { CurrentUser } from "@/types/inertia"

const COALESCE_WINDOW_MS = 30 * 60 * 1000

export function ActivityFeed<Event extends BaseActivityEvent>({
  events,
  eventsPath,
  currentUser,
  renderSummary,
  emptyMessage = "No activity yet. Decisions, edits, and your comments will appear here.",
}: {
  events: Event[]
  eventsPath: string
  currentUser: CurrentUser
  renderSummary: (event: Event) => React.ReactNode
  emptyMessage?: string
}) {
  const displayedEvents = React.useMemo(() => coalesceFieldUpdates(events), [events])

  if (displayedEvents.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-hairline px-4 py-8 text-center text-ink-muted">
        {emptyMessage}
      </div>
    )
  }

  return (
    <ol className="flex flex-col gap-12">
      {displayedEvents.map((event) => (
        <li key={event.id}>
          <ActivityEventCard
            event={event}
            eventsPath={eventsPath}
            currentUser={currentUser}
            renderSummary={renderSummary}
          />
        </li>
      ))}
    </ol>
  )
}

// Drops earlier `field_updated` events that were superseded by another update
// to the same field, by the same actor, within 30 minutes. The most recent
// event in each consecutive chain is kept so the timestamp reflects the
// latest activity. Other event types (status changes, comments, etc.) are
// always preserved. Assumes `events` is sorted oldest-first.
function coalesceFieldUpdates<Event extends BaseActivityEvent>(events: Event[]): Event[] {
  const lastKeptByKey = new Map<string, Event>()
  const drop = new Set<number>()

  for (const event of events) {
    const key = coalesceKey(event)
    if (key === null) continue
    const prior = lastKeptByKey.get(key)
    if (prior) {
      const dt = new Date(event.created_at).getTime() - new Date(prior.created_at).getTime()
      if (dt <= COALESCE_WINDOW_MS) {
        drop.add(prior.id)
      }
    }
    lastKeptByKey.set(key, event)
  }

  return drop.size === 0 ? events : events.filter((e) => !drop.has(e.id))
}

function coalesceKey(event: BaseActivityEvent): string | null {
  if (event.event_type !== "field_updated") return null
  const field = typeof event.metadata.field === "string" ? event.metadata.field : ""
  if (!field) return null
  return `${event.author_type}|${event.author_identifier}|${field}`
}
